import React, { useCallback, useEffect, useState } from 'react';

/**
 * Detects when a sentinel element has scrolled above the top of the
 * viewport (as opposed to `useIsSticky`, which detects a sticky element
 * pinning in place). Used to know when the full search header has
 * scrolled out so a condensed search bar can join the sticky filter row.
 * `entry.boundingClientRect.top < 0` distinguishes "scrolled past" from
 * the sentinel simply not being in the initial viewport (e.g. below the
 * fold on load), which would also report `isIntersecting: false`.
 */
const useSearchScrolledOut = (
	ref: React.MutableRefObject<HTMLDivElement | null>,
) => {
	const [isScrolledOut, setIsScrolledOut] = useState(false);

	const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
		const [entry] = entries;
		setIsScrolledOut(!entry.isIntersecting && entry.boundingClientRect.top < 0);
	}, []);

	const options: IntersectionObserverInit = {
		root: null,
		rootMargin: '0px',
		threshold: 0,
	};

	useEffect(() => {
		const node = ref?.current;
		if (!node) return;

		const observer = new IntersectionObserver(onIntersect, options);
		observer.observe(node);

		return () => observer.disconnect();
	}, [onIntersect, ref]);

	return isScrolledOut;
};

export default useSearchScrolledOut;
